import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '@/domains/auth/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { PhoneInputGroup } from '@/backoffice/components/ui/PhoneInputGroup';

type Mode = 'email' | 'phone';

const INPUT_CLASSES =
  'w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all';

export default function ForgotPasswordForm() {
  const [mode, setMode] = useState<Mode>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+224');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [channel, setChannel] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [debugLogin, setDebugLogin] = useState<string | null>(null);

  const loginValue =
    mode === 'email' ? email.trim() : `${countryCode}${phone.replace(/\D/g, '')}`;

  const isValid =
    mode === 'email'
      ? /\S+@\S+\.\S+/.test(email.trim())
      : phone.replace(/\D/g, '').length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      setError('Veuillez indiquer un e-mail ou un numéro valide.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await forgotPassword(loginValue);
      setSent(true);
      setChannel(res.channel ?? null);
      setDebugCode(res.debug_code ?? null);
      setDebugLogin(res.debug_login ?? null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Impossible d\'envoyer la demande.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col antialiased">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm px-4 h-16 flex items-center">
        <div className="max-w-6xl w-full mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-9 h-9 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-lg shadow-md shadow-teal-500/20">
              O
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-base tracking-tight text-slate-900 leading-none">ONAPILA</span>
              <span className="text-[9px] text-teal-600 font-medium tracking-widest uppercase mt-0.5">Livrer easy</span>
            </div>
          </Link>
          <Link to="/login" className="text-xs font-semibold text-teal-600 hover:underline">
            Connexion
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-6 sm:p-8">
            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                <i className="ri-lock-password-line text-xl" />
              </div>
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Mot de passe oublié</h1>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
                Indiquez l&apos;e-mail ou le téléphone de votre compte. Nous vous enverrons un lien ou un code pour
                créer un nouveau mot de passe.
              </p>
            </div>

            {sent ? (
              <div className="space-y-4">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800">
                  <p className="font-semibold mb-1">Demande enregistrée</p>
                  <p>
                    {channel === 'email'
                      ? 'Si un compte existe, un e-mail avec un lien de réinitialisation a été envoyé. Vérifiez aussi vos spams.'
                      : 'Si un compte existe, suivez les instructions reçues. Vous pouvez aussi réessayer avec votre e-mail.'}
                  </p>
                </div>

                {debugCode && (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-900">
                    <p className="font-bold mb-1">Mode debug</p>
                    <p>
                      Code : <span className="font-mono font-bold">{debugCode}</span>
                    </p>
                    {debugLogin && (
                      <Link
                        to={`/reset-password?login=${encodeURIComponent(debugLogin)}&token=${encodeURIComponent(debugCode)}`}
                        className="inline-block mt-2 text-teal-700 font-semibold underline"
                      >
                        Continuer vers la réinitialisation →
                      </Link>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Link
                    to="/login"
                    className="w-full py-3 text-center bg-teal-500 text-slate-950 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-teal-400"
                  >
                    Retour à la connexion
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setSent(false);
                      setDebugCode(null);
                    }}
                    className="w-full py-2.5 text-center text-xs font-semibold text-slate-500 hover:text-slate-700"
                  >
                    Renvoyer une demande
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex rounded-xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setMode('email')}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                      mode === 'email' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    E-mail
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('phone')}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                      mode === 'phone' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    Téléphone
                  </button>
                </div>

                {mode === 'email' ? (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      Adresse e-mail
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      autoComplete="email"
                      className={INPUT_CLASSES}
                      disabled={loading}
                    />
                  </div>
                ) : (
                  <PhoneInputGroup
                    label="Numéro de téléphone"
                    countryCode={countryCode}
                    onCountryCodeChange={setCountryCode}
                    value={phone}
                    onChange={(v) => setPhone(v)}
                    disabled={loading}
                    placeholder="620 00 00 01"
                    error={error || undefined}
                  />
                )}

                {error && (
                  <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !isValid}
                  className="w-full py-3 bg-teal-500 text-slate-950 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-teal-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-teal-500/10"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Envoi…
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" /> Envoyer le lien
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-4">
            Vous vous souvenez de votre mot de passe ?{' '}
            <Link to="/login" className="text-teal-600 font-semibold hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
